import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { TagColor } from '@/services/enums';

interface MultiSelectDropdownProps {
  label?: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: string[];
  loading?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  tagColor?: TagColor;
  getOptionLabel?: (option: string) => string; // 用于显示选项的标签（如果选项ID和显示文本不同）
  enableSearch?: boolean; // 是否启用搜索功能
  disabled?: boolean; // 是否禁用
  onSearch?: (keyword: string) => void; // 搜索回调
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  value,
  onChange,
  options,
  loading = false,
  placeholder = '点击选择...',
  searchPlaceholder = '搜索...',
  emptyText = '暂无数据',
  tagColor = 'red',
  getOptionLabel,
  enableSearch = true,
  disabled = false,
  onSearch,
}) => {
  const [searchText, setSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // 计算下拉菜单位置
  useEffect(() => {
    if (!showDropdown || !containerRef.current || disabled) return;

    const updatePosition = () => {
      const container = containerRef.current;
      if (!container) return;

      // const rect = container.getBoundingClientRect(); // Unused variable
      const inputArea = container.querySelector('div[class*="cursor-text"]') as HTMLElement;
      if (inputArea) {
        const inputRect = inputArea.getBoundingClientRect();
        setDropdownPosition({
          top: inputRect.bottom + window.scrollY + 4,
          left: inputRect.left + window.scrollX,
          width: inputRect.width,
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showDropdown, disabled]);

  // 点击外部关闭下拉框
  useEffect(() => {
    if (!showDropdown || disabled) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setShowDropdown(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown, disabled]);

  // 过滤选项
  const filteredOptions = options.filter((option) => {
    const searchLower = searchText.toLowerCase();
    const label = getOptionLabel ? getOptionLabel(option) : option;
    return label.toLowerCase().includes(searchLower) || option.toLowerCase().includes(searchLower);
  });

  // 标签颜色类
  const tagColorClasses = {
    red: 'bg-red-500/20 border-red-500/40 text-red-200 hover:bg-red-500/30 hover:text-red-400',
    blue: 'bg-blue-500/20 border-blue-500/40 text-blue-200 hover:bg-blue-500/30 hover:text-blue-400',
    green: 'bg-green-500/20 border-green-500/40 text-green-200 hover:bg-green-500/30 hover:text-green-400',
  };

  return (
    <div className="mb-4 relative" ref={containerRef} style={{ overflow: 'visible' }}>
      {label && <label className="block text-white/70 text-sm mb-2">{label}</label>}
      <div className="relative" style={{ overflow: 'visible', position: 'relative' }}>
        {/* 多选下拉框输入区域 */}
        <div
          onClick={(e) => {
            if (disabled) return;
            e.stopPropagation();
            setShowDropdown(!showDropdown);
          }}
          className={`w-full min-h-[42px] px-3 py-2 bg-white/10 border border-white/20 rounded-lg flex flex-wrap gap-2 items-center ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-text'
          }`}
        >
          <div className="flex flex-wrap gap-2 items-center flex-1">
            {value.length > 0 ? (
              value.map((item) => (
                <span
                  key={item}
                  onClick={(e) => {
                    if (disabled) return;
                    e.stopPropagation();
                    onChange(value.filter((v) => v !== item));
                  }}
                  className={`inline-flex items-center gap-1 px-2 py-1 border rounded text-xs ${
                    tagColorClasses[tagColor]
                  } ${disabled ? 'cursor-not-allowed' : ''}`}
                >
                  {getOptionLabel ? getOptionLabel(item) : item}
                  {!disabled && <span className="cursor-pointer">×</span>}
                </span>
              ))
            ) : (
              <span className="text-white/50 text-sm">{placeholder}</span>
            )}
            {enableSearch && (
              <input
                type="text"
                placeholder={value.length === 0 ? searchPlaceholder : ''}
                value={searchText}
                disabled={disabled}
                onChange={(e) => {
                  if (disabled) return;
                  e.stopPropagation();
                  setSearchText(e.target.value);
                  setShowDropdown(true);
                  if (onSearch) {
                    onSearch(e.target.value);
                  }
                }}
                onFocus={(e) => {
                  if (disabled) return;
                  e.stopPropagation();
                  setShowDropdown(true);
                }}
                onClick={(e) => e.stopPropagation()}
                className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-white text-sm placeholder:text-white/50 disabled:cursor-not-allowed"
              />
            )}
          </div>
          <svg
            className={`w-5 h-5 text-white/60 transition-transform flex-shrink-0 ${showDropdown ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {/* 下拉列表 - 使用 Portal 渲染到 body */}
        {showDropdown &&
          createPortal(
            <div
              ref={dropdownRef}
              className="fixed bg-gray-800 border border-white/20 rounded-lg max-h-60 overflow-y-auto shadow-lg"
              style={{
                position: 'fixed',
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                zIndex: 40,
                minHeight: '40px',
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {loading ? (
                <div className="px-3 py-2 text-white/50 text-sm text-center">加载中...</div>
              ) : filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-white/50 text-sm text-center">
                  {searchText ? '未找到匹配项' : emptyText}
                </div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = value.includes(option);
                  return (
                    <div
                      key={option}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isSelected) {
                          onChange(value.filter((v) => v !== option));
                        } else {
                          onChange([...value, option]);
                        }
                        setSearchText('');
                      }}
                      className={`px-3 py-2 cursor-pointer text-sm flex items-center justify-between ${
                        isSelected ? 'bg-green-500/20 text-green-200' : 'hover:bg-white/10 text-white'
                      }`}
                    >
                      <span>{getOptionLabel ? getOptionLabel(option) : option}</span>
                      {isSelected && (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  );
                })
              )}
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

export default MultiSelectDropdown;
